/**
 * Pure Table Tennis ITTF transition helpers (deterministic state machine).
 * Official Laws of Table Tennis — no badminton/tennis service-court parity.
 *
 * Doubles: persist server/receiver (+ team orders). Derive L/R courts every serve
 * (ADR-033 — persistent vs derived match state).
 */

/** @typedef {'A'|'B'} Side */
/** @typedef {{ side: Side, index: number }} PlayerRef */

/**
 * Deuce when both sides have reached pointsToWin − 1 (10–10 or 20–20).
 * @param {number} pointsToWin
 */
export function deuceThreshold(pointsToWin) {
  return Math.max(0, Number(pointsToWin) - 1);
}

/**
 * @param {number} a
 * @param {number} b
 * @param {number} pointsToWin
 */
export function isDeuce(a, b, pointsToWin) {
  const t = deuceThreshold(pointsToWin);
  return a >= t && b >= t;
}

/**
 * Serve change interval: 2 (11-pt) · 5 (21-pt) · 1 at deuce.
 * @param {number} pointsToWin
 * @param {number} a
 * @param {number} b
 */
export function resolveServeInterval(pointsToWin, a, b) {
  if (isDeuce(a, b, pointsToWin)) return 1;
  return Number(pointsToWin) >= 21 ? 5 : 2;
}

/**
 * Deciding game: change ends when first side reaches 5 (ITTF — all formats).
 */
export function endsChangeThreshold(_pointsToWin) {
  return 5;
}

/**
 * Final deciding game: both sides need one more game to win the match.
 * @param {object} match
 */
export function isDecidingGame(match) {
  const bestOf = match?.bestOf || 5;
  const need = Math.ceil(bestOf / 2);
  const games = match?.games || [];
  const wonA = games.filter((g) => g.gameWinner === "A").length;
  const wonB = games.filter((g) => g.gameWinner === "B").length;
  return wonA === need - 1 && wonB === need - 1;
}

/**
 * True once when max score first hits 5 in a deciding game.
 * @param {object} match
 * @param {object} game
 */
export function shouldChangeEnds(match, game) {
  if (!match || !game || game.gameWinner) return false;
  if (match.endsChangeDone) return false;
  if (!isDecidingGame(match)) return false;
  const threshold = endsChangeThreshold(game.pointsToWin);
  const a = game.sideA?.score ?? 0;
  const b = game.sideB?.score ?? 0;
  return Math.max(a, b) === threshold && Math.min(a, b) < threshold;
}

function otherSide(side) {
  return side === "A" ? "B" : "A";
}

/** Partner index within a doubles pair (0 ↔ 1). */
export function partnerIndex(index) {
  return Number(index) === 0 ? 1 : 0;
}

/**
 * Normalize a 2-player order array (first = preferred first server/receiver).
 * @param {number[]|null|undefined} order
 * @returns {[number, number]}
 */
export function normalizePairOrder(order) {
  if (Array.isArray(order) && order.length >= 2) {
    const a = Number(order[0]) === 1 ? 1 : 0;
    const b = Number(order[1]) === 1 ? 1 : 0;
    if (a !== b) return [a, b];
  }
  return [0, 1];
}

/**
 * ITTF doubles service change:
 * 1. Previous receiver becomes the new server
 * 2. New receiver is the partner of the previous server
 *
 * @param {object} game
 */
export function advanceDoublesService(game) {
  const server = game.currentServer || {
    side: game.servingSide || "A",
    index: game.serverIndex ?? 0,
  };
  const receiver = game.currentReceiver || {
    side: otherSide(server.side),
    index: game.receiverIndex ?? 0,
  };
  const newServer = { side: receiver.side, index: receiver.index };
  const newReceiver = { side: server.side, index: partnerIndex(server.index) };
  return {
    ...game,
    servingSide: newServer.side,
    serverIndex: newServer.index,
    receiverIndex: newReceiver.index,
    currentServer: newServer,
    currentReceiver: newReceiver,
    serveCount: 0,
    lastPlayerHit: null,
    lastHitIndexBySide: {},
  };
}

/**
 * Derive L/R service-court occupants for the upcoming serve.
 * Never persist — recompute from currentServer / currentReceiver (ADR-033).
 *
 * Server → right · server partner → left
 * Receiver → right · receiver partner → left
 *
 * @param {PlayerRef} server
 * @param {PlayerRef} receiver
 * @returns {{ A: { right: number, left: number }, B: { right: number, left: number } }}
 */
export function deriveDoublesCourtLayout(server, receiver) {
  /** @type {{ A: { right: number, left: number }, B: { right: number, left: number } }} */
  const layout = {
    A: { right: 0, left: 1 },
    B: { right: 0, left: 1 },
  };
  if (server?.side) {
    layout[server.side] = {
      right: server.index,
      left: partnerIndex(server.index),
    };
  }
  if (receiver?.side) {
    layout[receiver.side] = {
      right: receiver.index,
      left: partnerIndex(receiver.index),
    };
  }
  return layout;
}

/**
 * Advance service after a point was scored (scores already updated on game).
 * Who won the rally does not affect who serves — only serveCount / interval.
 * @param {object} game
 * @param {'singles'|'doubles'} mode
 */
export function advanceService(game, mode = "singles") {
  const a = game.sideA?.score ?? 0;
  const b = game.sideB?.score ?? 0;
  const pointsToWin = game.pointsToWin ?? 11;
  const interval = resolveServeInterval(pointsToWin, a, b);
  const serveCount = (game.serveCount ?? 0) + 1;

  let next = {
    ...game,
    serveInterval: interval,
    serveCount,
    lastPlayerHit: null,
    lastHitIndexBySide: {},
  };

  if (serveCount >= interval) {
    if (mode === "doubles") {
      next = advanceDoublesService({ ...next, serveCount: 0 });
      next.serveInterval = resolveServeInterval(pointsToWin, a, b);
    } else {
      const newSide = game.servingSide === "A" ? "B" : "A";
      next = {
        ...next,
        servingSide: newSide,
        serveCount: 0,
        serveInterval: resolveServeInterval(pointsToWin, a, b),
        currentServer: { side: newSide, index: 0 },
        currentReceiver: { side: game.servingSide, index: 0 },
      };
    }
  } else if (mode === "singles") {
    next.currentServer = { side: next.servingSide, index: 0 };
    next.currentReceiver = {
      side: next.servingSide === "A" ? "B" : "A",
      index: 0,
    };
  } else {
    next.currentServer = {
      side: next.servingSide,
      index: next.serverIndex ?? 0,
    };
    next.currentReceiver = next.currentReceiver || {
      side: next.servingSide === "A" ? "B" : "A",
      index: next.receiverIndex ?? 0,
    };
  }

  delete next.serveHand;
  delete next.rotation;
  delete next.rotationIndex;
  return next;
}

/**
 * Doubles: partners must alternate every contact after the serve.
 * @param {object} game
 * @param {Side} side
 * @param {number} index
 */
export function validateDoublesHit(game, side, index) {
  const last = game.lastPlayerHit;
  if (last && last.side === side) {
    return { ok: false, reason: "same_team_consecutive" };
  }
  const prevIndex = game.lastHitIndexBySide?.[side];
  if (prevIndex != null && Number(prevIndex) === Number(index)) {
    return { ok: false, reason: "partner_not_alternating" };
  }
  return { ok: true };
}

/**
 * @param {object} game
 * @param {Side} side
 * @param {number} index
 */
export function applyDoublesHit(game, side, index) {
  return {
    ...game,
    lastPlayerHit: { side, index },
    lastHitIndexBySide: {
      ...(game.lastHitIndexBySide || {}),
      [side]: index,
    },
  };
}

/**
 * Build initial doubles server/receiver from team orders.
 * @param {Side} servingSide
 * @param {number[]} orderServing — first = initial server index on serving side
 * @param {number[]} orderReceiving — first = initial receiver index on receiving side
 */
export function initialDoublesRoles(servingSide, orderServing, orderReceiving) {
  const srvOrder = normalizePairOrder(orderServing);
  const recvOrder = normalizePairOrder(orderReceiving);
  const recvSide = otherSide(servingSide);
  return {
    orderA: servingSide === "A" ? srvOrder : recvOrder,
    orderB: servingSide === "B" ? srvOrder : recvOrder,
    currentServer: { side: servingSide, index: srvOrder[0] },
    currentReceiver: { side: recvSide, index: recvOrder[0] },
  };
}

/**
 * Migrate older game blobs → current schema.
 * @param {object|null|undefined} game
 * @param {'singles'|'doubles'} [mode]
 */
export function migrateGameState(game, mode = "singles") {
  if (!game) return game;
  const a = game.sideA?.score ?? 0;
  const b = game.sideB?.score ?? 0;
  const pointsToWin = game.pointsToWin ?? 11;
  const serveCount =
    game.serveCount != null ? game.serveCount : game.serveStreak != null ? game.serveStreak : 0;
  const serveInterval =
    game.serveInterval != null
      ? game.serveInterval
      : resolveServeInterval(pointsToWin, a, b);

  let next = {
    ...game,
    serveCount,
    serveInterval,
    deuceThreshold: game.deuceThreshold ?? deuceThreshold(pointsToWin),
    lastPlayerHit: game.lastPlayerHit || null,
    lastHitIndexBySide: game.lastHitIndexBySide || {},
  };
  delete next.serveStreak;
  delete next.serveHand;

  if (mode === "doubles") {
    // Legacy rotation cycle → current server/receiver
    if (!next.currentServer && next.rotation) {
      const i = next.rotationIndex ?? 0;
      const cycle = next.rotation;
      const server = cycle[i % cycle.length];
      const receiver = cycle[(i + 1) % cycle.length];
      next.currentServer = server;
      next.currentReceiver = receiver;
      next.servingSide = server.side;
      next.serverIndex = server.index;
      next.receiverIndex = receiver.index;
    }
    next.orderA = normalizePairOrder(next.orderA || next.servingOrderA);
    next.orderB = normalizePairOrder(next.orderB || next.servingOrderB);
    next.currentServer = next.currentServer || {
      side: next.servingSide || "A",
      index: next.serverIndex || 0,
    };
    next.currentReceiver = next.currentReceiver || {
      side: otherSide(next.currentServer.side),
      index: next.receiverIndex || 0,
    };
    next.servingSide = next.currentServer.side;
    next.serverIndex = next.currentServer.index;
    next.receiverIndex = next.currentReceiver.index;
  } else {
    next.currentServer = next.currentServer || {
      side: next.servingSide || "A",
      index: 0,
    };
    next.currentReceiver = next.currentReceiver || {
      side: otherSide(next.servingSide || "A"),
      index: 0,
    };
  }

  delete next.rotation;
  delete next.rotationIndex;
  return next;
}

/** @deprecated use migrateGameState */
export const migrateGameToV2 = migrateGameState;

/**
 * @param {object} game
 * @param {'singles'|'doubles'} [mode]
 */
export function scoreMetaForGame(game, mode = "singles") {
  if (!game) return "";
  const a = game.sideA?.score ?? 0;
  const b = game.sideB?.score ?? 0;
  const deuce = isDeuce(a, b, game.pointsToWin);
  const interval = resolveServeInterval(game.pointsToWin, a, b);
  const rule = deuce
    ? "Deuce — alternate every point"
    : interval === 5
      ? "Serve every 5 points"
      : "Serve every 2 points";
  const diagonal = mode === "doubles" ? " · Diagonal R→R" : "";
  const who =
    mode === "doubles" && game.currentServer
      ? `Serve ${game.currentServer.side}${game.currentServer.index}`
      : `Serve ${game.servingSide}`;
  return `${who}${diagonal} · ${rule}`;
}

// --- Legacy aliases kept for tests that imported the old rotation API ---

/** @deprecated ITTF doubles uses advanceDoublesService */
export function buildDefaultDoublesRotation() {
  return [
    { side: "A", index: 0 },
    { side: "B", index: 0 },
    { side: "A", index: 1 },
    { side: "B", index: 1 },
  ];
}

/** @deprecated */
export function serverFromRotation(rotation, rotationIndex) {
  const cycle = rotation?.length ? rotation : buildDefaultDoublesRotation();
  const i = ((rotationIndex % cycle.length) + cycle.length) % cycle.length;
  return {
    server: cycle[i],
    receiver: cycle[(i + 1) % cycle.length],
    rotationIndex: i,
  };
}

/** @deprecated use advanceDoublesService */
export function advanceDoublesRotation(game) {
  return advanceDoublesService(game);
}
